import { useState, useEffect, useRef } from "react";
import * as THREE from "three";

/* ─── DATA ─────────────────────────────────────────────────── */
const NAV = [
  { label: "About", id: "about" },
  { label: "Skills", id: "skills" },
  { label: "Projects", id: "projects" },
  { label: "Hackathons", id: "hackathons" },
  { label: "Contact", id: "contact" },
];

// Updated with your real GitHub projects
const ML_PROJECTS = [
  {
    title: "Malicious URL Classifier",
    desc: "Machine learning model to detect and classify malicious URLs using advanced feature extraction and ensemble methods for cybersecurity applications.",
    tags: ["Classification", "Cybersecurity", "Scikit-learn", "Python"],
    link: "https://github.com/AkashBabuYash/Malicious-URL-Classifier",
    icon: "⚠️",
  },
  {
    title: "Climate Change Prediction",
    desc: "Time-series forecasting model analyzing climate patterns and predicting future climate indicators using regression techniques and historical data.",
    tags: ["Time Series", "Regression", "Pandas", "Python"],
    link: "https://github.com/AkashBabuYash/climatechangeprediction",
    icon: "🌍",
  },
  {
    title: "Loan Approval Predictor",
    desc: "ML-based system that predicts loan eligibility based on applicant financial history, credit score, and other key features with high accuracy.",
    tags: ["Classification", "Scikit-learn", "Feature Engineering"],
    link: "https://github.com/AkashBabuYash/loan_approved__ml_project",
    icon: "🏦",
  },
  {
    title: "Wine Quality Detection",
    desc: "ML model predicting wine quality scores from physicochemical properties. Compares multiple classifiers with hyperparameter tuning and feature importance analysis.",
    tags: ["scikit-learn", "Classification", "Feature Engineering", "Python"],
    link: "https://github.com/AkashBabuYash/winequalityprediction",
    icon: "🍷",
  },
  {
    title: "Resume Classifier",
    desc: "NLP-powered system that automatically categorizes resumes into job domains using text classification and feature extraction techniques.",
    tags: ["NLP", "Text Classification", "Python", "Machine Learning"],
    link: "https://github.com/AkashBabuYash/resume-classify",
    icon: "📄",
  },
];

const DL_PROJECTS = [
  {
    title: "Brain Tumor Detection",
    desc: "Deep learning model using fine-tuned BileNet architecture for accurate brain tumor classification from MRI scans with high precision and recall.",
    tags: ["CNN", "Medical Imaging", "Fine-tuning", "TensorFlow"],
    link: "https://github.com/AkashBabuYash/Braintumordetection",
    icon: "🧠",
  },
  {
    title: "Pneumonia Detection",
    desc: "Convolutional Neural Network for detecting pneumonia from chest X-ray images using transfer learning and data augmentation techniques.",
    tags: ["CNN", "Medical AI", "Classification", "Keras"],
    link: "https://github.com/AkashBabuYash/Pneumonia-detection",
    icon: "🫁",
  },
];

const GENAI_PROJECTS = [
  {
    title: "AI Code Generator",
    desc: "Intelligent code generation agent built with LangChain and LangGraph. Uses multi-step reasoning workflows to produce clean, well-documented code from natural language prompts with automatic error correction.",
    tags: ["LangChain", "LangGraph", "Generative AI", "Python", "Agentic"],
    link: "https://github.com/AkashBabuYash/smartaicodegenerator",
    icon: "🤖",
  },
  {
    title: "AI PPT Generator",
    desc: "Automated PowerPoint presentation generator powered by LangChain and LangGraph agents. Takes a topic and generates a complete, professionally structured PPTX with slides, content, and formatting.",
    tags: ["LangChain", "LangGraph", "python-pptx", "FastAPI", "Agentic"],
    link: "https://github.com/AkashBabuYash/pptgenerator",
    icon: "📊",
  },
  {
    title: "RAG Document Q&A System",
    desc: "Retrieval-Augmented Generation pipeline for querying documents using LangChain, ChromaDB vector store, and LLM integration for intelligent answers.",
    tags: ["RAG", "LangChain", "ChromaDB", "LLM"],
    link: "https://github.com/AkashBabuYash/rag",
    icon: "📚",
  },
  {
    title: "ERA-Tech Chat Application",
    desc: "Real-time MERN stack messaging platform with Socket.io for instant communication, user authentication, and a clean modern UI.",
    tags: ["MERN", "Socket.io", "React", "Node.js"],
    link: "https://era-techlimited.netlify.app",
    icon: "🌐",
  },
  {
    title: "Group Chat Application",
    desc: "Real-time group messaging platform with user authentication, room-based chat, and instant message delivery using WebSocket technology.",
    tags: ["MERN", "Socket.io", "React", "Node.js"],
    link: "https://github.com/AkashBabuYash/groupchatapplication",
    icon: "💬",
  },
];

const PROJECT_SECTIONS = [
  { id: "ml",    label: "Machine Learning",      color: "#4ade80", glyph: "ML", projects: ML_PROJECTS },
  { id: "dl",    label: "Deep Learning",          color: "#60a5fa", glyph: "DL", projects: DL_PROJECTS },
  { id: "genai", label: "Generative & Agentic AI", color: "#c084fc", glyph: "AI", projects: GENAI_PROJECTS },
];

// Flat list of ALL projects for the auto-display grid
const ALL_PROJECTS = [
  ...ML_PROJECTS.map((p) => ({ ...p, category: "Machine Learning", color: "#4ade80" })),
  ...DL_PROJECTS.map((p) => ({ ...p, category: "Deep Learning", color: "#60a5fa" })),
  ...GENAI_PROJECTS.map((p) => ({ ...p, category: "Generative & Agentic AI", color: "#c084fc" })),
];

const SKILLS = [
  { icon: "🤖", name: "Machine Learning", sub: "Supervised & Unsupervised" },
  { icon: "🧠", name: "Deep Learning", sub: "CNN · RNN · LSTM · ANN" },
  { icon: "✨", name: "Generative AI", sub: "LangChain · HuggingFace" },
  { icon: "🕵️", name: "Agentic AI", sub: "LangGraph · HF Agents" },
  { icon: "🌐", name: "MERN Stack", sub: "React · Node · MongoDB" },
  { icon: "♨️", name: "DSA in Java", sub: "Algorithms & Structures" },
  { icon: "📦", name: "RAG / CRAG", sub: "Self-RAG · Advanced RAG" },
  { icon: "🔬", name: "LLM Fine-tuning", sub: "Custom Model Training" },
];

const HACKATHONS = [
  { icon: "🏆", name: "HackX Hackathon", project: "AI Agent", badge: "Participated" },
  { icon: "🚀", name: "HackFest Hackathon", project: "SmartStudyPlan", badge: "Participated" },
  { icon: "🧩", name: "MMMUT Hackathon", project: "Smart Grievance Hub", badge: "Participated" },
];

/* ─── THREE.JS SCENE ────────────────────────────────────────── */
function ThreeScene({ scrollY }) {
  const canvasRef = useRef(null);
  const sceneRef = useRef({});

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 4;

    // Particles
    const particleCount = 1200;
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
    }
    const pgeo = new THREE.BufferGeometry();
    pgeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const pmat = new THREE.PointsMaterial({ color: 0x7c6af7, size: 0.03, transparent: true, opacity: 0.7 });
    const particles = new THREE.Points(pgeo, pmat);
    scene.add(particles);

    // Floating torus
    const tgeo = new THREE.TorusGeometry(1.2, 0.35, 32, 100);
    const tmat = new THREE.MeshStandardMaterial({
      color: 0x7c6af7,
      wireframe: true,
      transparent: true,
      opacity: 0.25,
    });
    const torus = new THREE.Mesh(tgeo, tmat);
    torus.position.x = 2.5;
    scene.add(torus);

    // Icosahedron
    const igeo = new THREE.IcosahedronGeometry(0.7, 1);
    const imat = new THREE.MeshStandardMaterial({
      color: 0x06d6a0,
      wireframe: true,
      transparent: true,
      opacity: 0.3,
    });
    const icosa = new THREE.Mesh(igeo, imat);
    icosa.position.set(-2.5, 1, -1);
    scene.add(icosa);

    // Octahedron
    const ogeo = new THREE.OctahedronGeometry(0.5);
    const omat = new THREE.MeshStandardMaterial({
      color: 0xf72585,
      wireframe: true,
      transparent: true,
      opacity: 0.25,
    });
    const octa = new THREE.Mesh(ogeo, omat);
    octa.position.set(0, -2, 0.5);
    scene.add(octa);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambient);
    const point = new THREE.PointLight(0x7c6af7, 2, 10);
    point.position.set(2, 2, 2);
    scene.add(point);

    sceneRef.current = { renderer, scene, camera, particles, torus, icosa, octa };

    const resize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", resize);

    let frame;
    const clock = new THREE.Clock();
    const animate = () => {
      frame = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      particles.rotation.y = t * 0.04;
      particles.rotation.x = t * 0.02;
      torus.rotation.x = t * 0.3;
      torus.rotation.y = t * 0.5;
      icosa.rotation.x = t * 0.4;
      icosa.rotation.z = t * 0.25;
      octa.rotation.y = t * 0.6;
      octa.rotation.x = t * 0.3;
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      renderer.dispose();
    };
  }, []);

  // Parallax on scroll
  useEffect(() => {
    if (sceneRef.current.camera) {
      sceneRef.current.camera.position.y = -scrollY * 0.002;
    }
  }, [scrollY]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
}

/* ─── ANIMATED PROFILE ──────────────────────────────────────── */
function AnimatedProfile() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(340, 340);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.z = 3.5;

    // Outer orbit ring
    const rGeo = new THREE.TorusGeometry(1.55, 0.018, 16, 120);
    const rMat = new THREE.MeshBasicMaterial({ color: 0x7c6af7, transparent: true, opacity: 0.8 });
    const ring1 = new THREE.Mesh(rGeo, rMat);
    scene.add(ring1);

    // Second ring
    const rGeo2 = new THREE.TorusGeometry(1.75, 0.008, 16, 120);
    const rMat2 = new THREE.MeshBasicMaterial({ color: 0x06d6a0, transparent: true, opacity: 0.4 });
    const ring2 = new THREE.Mesh(rGeo2, rMat2);
    ring2.rotation.x = 0.3;
    scene.add(ring2);

    // Orbiting spheres
    const orbitGroup = new THREE.Group();
    scene.add(orbitGroup);
    const orbColors = [0x7c6af7, 0x06d6a0, 0xf72585, 0xffd166];
    const orbMeshes = orbColors.map((c, i) => {
      const geo = new THREE.SphereGeometry(0.07, 16, 16);
      const mat = new THREE.MeshBasicMaterial({ color: c });
      const mesh = new THREE.Mesh(geo, mat);
      const angle = (i / orbColors.length) * Math.PI * 2;
      mesh.position.set(Math.cos(angle) * 1.55, Math.sin(angle) * 1.55, 0);
      orbitGroup.add(mesh);
      return mesh;
    });

    // Outer glow plane (sprite)
    const glowGeo = new THREE.PlaneGeometry(3.2, 3.2);
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0x7c6af7,
      transparent: true,
      opacity: 0.06,
      side: THREE.DoubleSide,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    scene.add(glow);

    // Avatar sphere placeholder
    const faceGeo = new THREE.SphereGeometry(1.1, 64, 64);
    const faceMat = new THREE.MeshStandardMaterial({
      color: 0x1e1e2e,
      roughness: 0.2,
      metalness: 0.8,
      wireframe: false,
    });
    const faceSphere = new THREE.Mesh(faceGeo, faceMat);
    scene.add(faceSphere);

    // Ambient light
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const pl = new THREE.PointLight(0x7c6af7, 3, 8);
    pl.position.set(2, 2, 2);
    scene.add(pl);
    const pl2 = new THREE.PointLight(0x06d6a0, 1.5, 8);
    pl2.position.set(-2, -1, 1);
    scene.add(pl2);

    const handleMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 2,
        y: -((e.clientY - rect.top) / rect.height - 0.5) * 2,
      };
    };
    window.addEventListener("mousemove", handleMouse);

    let frame;
    const clock = new THREE.Clock();
    const animate = () => {
      frame = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      ring1.rotation.z = t * 0.4;
      ring2.rotation.z = -t * 0.25;
      orbitGroup.rotation.z = t * 0.6;

      // Smooth mouse-follow tilt
      faceSphere.rotation.y += (mx * 0.4 - faceSphere.rotation.y) * 0.05;
      faceSphere.rotation.x += (-my * 0.3 - faceSphere.rotation.x) * 0.05;

      pl.position.x = mx * 3;
      pl.position.y = my * 3;

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("mousemove", handleMouse);
      renderer.dispose();
    };
  }, []);

  return (
    <div style={{ position: "relative", width: 340, height: 340 }}>
      <canvas
        ref={canvasRef}
        style={{ width: 340, height: 340, borderRadius: "50%" }}
      />
      {/* AB initials text overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
          fontSize: "5rem",
          fontWeight: 900,
          background: "linear-gradient(135deg, #7c6af7, #06d6a0)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          pointerEvents: "none",
          letterSpacing: "-2px",
        }}
      >
        AB
      </div>
    </div>
  );
}

/* ─── PROJECT CARD ──────────────────────────────────────────── */
function ProjectCard({ project, color, categoryLabel }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "#1a1a28" : "#13131e",
        border: `1px solid ${hovered ? color + "66" : "rgba(255,255,255,0.07)"}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 16,
        padding: "1.6rem",
        transition: "all 0.3s ease",
        transform: hovered ? "translateY(-4px) translateX(3px)" : "none",
        boxShadow: hovered ? `0 12px 40px ${color}22` : "none",
        cursor: "default",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>{project.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: "1.05rem", color: "#e8e8f0" }}>
            {project.title}
          </div>
          {categoryLabel && (
            <span style={{ fontSize: "0.68rem", color: color, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, opacity: 0.85 }}>
              {categoryLabel}
            </span>
          )}
        </div>
      </div>
      <div style={{ color: "#888899", fontSize: "0.88rem", lineHeight: 1.7, marginBottom: "1rem" }}>
        {project.desc}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "space-between", alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {project.tags.map((t) => (
            <span
              key={t}
              style={{
                background: color + "15",
                color,
                border: `1px solid ${color}33`,
                borderRadius: 6,
                fontSize: "0.72rem",
                fontFamily: "'JetBrains Mono', monospace",
                padding: "3px 9px",
              }}
            >
              {t}
            </span>
          ))}
        </div>
        {project.link && (
          <a
            href={project.link}
            target="_blank"
            rel="noreferrer"
            style={{
              color,
              textDecoration: "none",
              fontSize: "0.8rem",
              fontWeight: 600,
              border: `1px solid ${color}44`,
              padding: "4px 12px",
              borderRadius: 8,
              transition: "all 0.2s",
            }}
          >
            Live ↗
          </a>
        )}
      </div>
    </div>
  );
}

/* ─── SKILL CARD ────────────────────────────────────────────── */
function SkillCard({ skill }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "#1e1e2e" : "#13131e",
        border: `1px solid ${hov ? "rgba(124,106,247,0.4)" : "rgba(255,255,255,0.07)"}`,
        borderRadius: 18,
        padding: "1.5rem 1rem",
        textAlign: "center",
        transition: "all 0.3s",
        transform: hov ? "translateY(-6px) scale(1.02)" : "none",
        boxShadow: hov ? "0 12px 30px rgba(124,106,247,0.15)" : "none",
        cursor: "default",
      }}
    >
      <span style={{ fontSize: "2.4rem", display: "block", marginBottom: "0.75rem" }}>{skill.icon}</span>
      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#e8e8f0" }}>{skill.name}</div>
      <div style={{ fontSize: "0.74rem", color: "#888899", marginTop: 4 }}>{skill.sub}</div>
    </div>
  );
}

/* ─── MAIN PORTFOLIO ────────────────────────────────────────── */
export default function Portfolio() {
  const [scrollY, setScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active section tracking
  useEffect(() => {
    const sections = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }),
      { rootMargin: "-40% 0px -55% 0px" }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const navBg = scrollY > 50 ? "rgba(10,10,15,0.92)" : "rgba(10,10,15,0.6)";

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&family=Outfit:wght@300;400;500;600&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    html { scroll-behavior:smooth; }
    body { background:#0a0a0f; color:#e8e8f0; font-family:'Outfit',sans-serif; overflow-x:hidden; }
    ::selection { background:#7c6af7; color:#fff; }
    ::-webkit-scrollbar { width:3px; }
    ::-webkit-scrollbar-track { background:#0a0a0f; }
    ::-webkit-scrollbar-thumb { background:linear-gradient(#7c6af7,#06d6a0); border-radius:2px; }
    section { max-width:1100px; margin:0 auto; padding:100px 2rem; }
    .reveal { opacity:0; transform:translateY(30px); transition:opacity 0.7s ease, transform 0.7s ease; }
    .reveal.vis { opacity:1; transform:none; }
    .section-eyebrow { font-family:'JetBrains Mono',monospace; font-size:0.72rem; color:#06d6a0; text-transform:uppercase; letter-spacing:0.18em; margin-bottom:0.75rem; display:block; }
    .section-h { font-family:'Syne',sans-serif; font-size:clamp(2rem,4vw,2.8rem); font-weight:800; line-height:1.1; margin-bottom:2.5rem; }
    .grad-text { background:linear-gradient(135deg,#7c6af7,#06d6a0); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
    .divider { width:100%; height:1px; background:rgba(255,255,255,0.07); }
    @media(max-width:900px) { .hide-mob { display:none !important; } }
    /* ── RESPONSIVE GRIDS ── */
    .grid-2col { display:grid; grid-template-columns:1fr 1fr; gap:3rem; }
    .grid-4col { display:grid; grid-template-columns:repeat(4,1fr); gap:1.25rem; margin-bottom:4rem; }
    .grid-knowledge { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1.25rem; }
    .grid-projects { display:grid; grid-template-columns:repeat(auto-fill,minmax(340px,1fr)); gap:1.25rem; }
    .grid-hackathons { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1.25rem; }
    .hero-grid { display:grid; grid-template-columns:1fr auto; gap:4rem; align-items:center; }
    .hero-btns { display:flex; gap:12px; flex-wrap:wrap; }
    .hero-socials { display:flex; gap:10px; margin-top:2rem; }
    .about-stats { display:grid; grid-template-columns:1fr 1fr; gap:1rem; }
    .contact-links-grid { display:grid; grid-template-columns:1fr 1fr; gap:3rem; }
    @media(max-width:900px) {
      section { padding:70px 1.25rem; }
      .grid-4col { grid-template-columns:repeat(2,1fr); }
      .hero-grid { grid-template-columns:1fr; gap:2rem; }
      .contact-links-grid { grid-template-columns:1fr; gap:1.5rem; }
    }
    @media(max-width:600px) {
      section { padding:60px 1rem; }
      .section-h { margin-bottom:1.5rem; }
      .grid-2col { grid-template-columns:1fr; gap:1.5rem; }
      .grid-4col { grid-template-columns:repeat(2,1fr); gap:0.85rem; margin-bottom:2rem; }
      .grid-projects { grid-template-columns:1fr; }
      .grid-hackathons { grid-template-columns:1fr; }
      .grid-knowledge { grid-template-columns:1fr; }
      .hero-grid { grid-template-columns:1fr; gap:1.5rem; }
      .hero-btns { flex-direction:column; }
      .hero-btns button, .hero-btns a { width:100%; text-align:center; }
      .hero-socials { flex-wrap:wrap; gap:8px; }
      .about-stats { grid-template-columns:1fr 1fr; gap:0.75rem; }
      .contact-links-grid { grid-template-columns:1fr; gap:1rem; }
    }
  `;

  // Reveal effect
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("vis"); }),
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });

  return (
    <>
      <style>{css}</style>

      {/* ── NAV ── */}
      <nav
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "1rem 2.5rem",
          background: navBg,
          backdropFilter: "blur(24px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          transition: "background 0.3s",
        }}
      >
        <span
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          style={{
            fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "1.35rem",
            background: "linear-gradient(135deg,#7c6af7,#06d6a0)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            cursor: "pointer",
          }}
        >
          Akash Babu
        </span>

        {/* Desktop links */}
        <div className="hide-mob" style={{ display: "flex", gap: 4 }}>
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => scrollTo(n.id)}
              style={{
                background: activeSection === n.id ? "rgba(124,106,247,0.12)" : "transparent",
                border: `1px solid ${activeSection === n.id ? "rgba(124,106,247,0.3)" : "transparent"}`,
                color: activeSection === n.id ? "#a78bfa" : "#888899",
                borderRadius: 8, padding: "6px 14px",
                fontFamily: "'Outfit',sans-serif", fontSize: "0.83rem", fontWeight: 500,
                letterSpacing: "0.04em", cursor: "pointer", transition: "all 0.2s",
              }}
            >
              {n.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); scrollTo("contact"); }}
            style={{
              padding: "8px 20px",
              background: "linear-gradient(135deg,#7c6af7,#a78bfa)",
              color: "#fff", borderRadius: 8, fontSize: "0.83rem",
              fontWeight: 600, textDecoration: "none", border: "none",
              fontFamily: "'Outfit',sans-serif",
            }}
          >
            Hire Me
          </a>
          {/* Hamburger for mobile only visible via CSS */}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              background: menuOpen ? "rgba(124,106,247,0.15)" : "transparent",
              border: `1px solid ${menuOpen ? "rgba(124,106,247,0.4)" : "rgba(255,255,255,0.15)"}`,
              borderRadius: 10, width: 42, height: 42, cursor: "pointer",
              display: "none",
              flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5,
              transition: "all 0.25s",
            }}
            className="hamburger-btn"
            aria-label="Toggle navigation"
          >
            <span style={{ width: 18, height: 2, background: menuOpen ? "#a78bfa" : "#e8e8f0", display: "block", transition: "all 0.3s", transform: menuOpen ? "rotate(45deg) translate(3px, 3px)" : "none", borderRadius: 2 }} />
            <span style={{ width: 18, height: 2, background: menuOpen ? "#a78bfa" : "#e8e8f0", display: "block", transition: "all 0.3s", opacity: menuOpen ? 0 : 1, borderRadius: 2 }} />
            <span style={{ width: 18, height: 2, background: menuOpen ? "#a78bfa" : "#e8e8f0", display: "block", transition: "all 0.3s", transform: menuOpen ? "rotate(-45deg) translate(3px, -3px)" : "none", borderRadius: 2 }} />
          </button>
        </div>
      </nav>

      {/* Mobile slide-down menu */}
      <div
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 199,
          background: "rgba(10,10,15,0.98)",
          backdropFilter: "blur(28px)",
          borderBottom: menuOpen ? "1px solid rgba(124,106,247,0.2)" : "1px solid transparent",
          transform: menuOpen ? "translateY(0)" : "translateY(-110%)",
          transition: "transform 0.38s cubic-bezier(0.4,0,0.2,1), border-color 0.38s",
          paddingTop: "72px",
          paddingBottom: "2rem",
        }}
      >
        {/* Close button inside menu */}
        <button
          onClick={() => setMenuOpen(false)}
          style={{
            position: "absolute", top: "1.1rem", right: "1.5rem",
            width: 38, height: 38, borderRadius: 10,
            background: "rgba(124,106,247,0.12)",
            border: "1px solid rgba(124,106,247,0.3)",
            color: "#a78bfa", fontSize: "1.2rem", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1, fontWeight: 700, transition: "all 0.2s",
          }}
          aria-label="Close menu"
        >✕</button>

        <div style={{ maxWidth: 480, margin: "0 auto", padding: "0 2rem", display: "flex", flexDirection: "column", gap: 4 }}>
          {NAV.map((n, idx) => (
            <button
              key={n.id}
              onClick={() => scrollTo(n.id)}
              style={{
                background: activeSection === n.id ? "rgba(124,106,247,0.1)" : "transparent",
                border: `1px solid ${activeSection === n.id ? "rgba(124,106,247,0.25)" : "transparent"}`,
                borderRadius: 12,
                fontFamily: "'Outfit',sans-serif", fontSize: "1.05rem", fontWeight: 600,
                color: activeSection === n.id ? "#a78bfa" : "#c8c8d8",
                cursor: "pointer", padding: "14px 18px",
                textAlign: "left",
                display: "flex", alignItems: "center", gap: 12,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(124,106,247,0.08)"; e.currentTarget.style.color = "#a78bfa"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = activeSection === n.id ? "rgba(124,106,247,0.1)" : "transparent"; e.currentTarget.style.color = activeSection === n.id ? "#a78bfa" : "#c8c8d8"; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: activeSection === n.id ? "#7c6af7" : "#444455", flexShrink: 0, transition: "all 0.2s" }} />
              {n.label}
            </button>
          ))}
          <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "0.75rem 0" }} />
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); scrollTo("contact"); }}
            style={{
              display: "block", textAlign: "center",
              padding: "13px", borderRadius: 12,
              background: "linear-gradient(135deg,#7c6af7,#a78bfa)",
              color: "#fff", fontWeight: 700, fontSize: "0.95rem",
              textDecoration: "none", fontFamily: "'Outfit',sans-serif",
            }}
          >
            Hire Me
          </a>
        </div>
      </div>

      {/* ── HERO ── */}
      <div
        style={{
          minHeight: "100vh", position: "relative",
          display: "flex", alignItems: "center",
          overflow: "hidden",
        }}
      >
        {/* Three.js background — covers full viewport always */}
        <ThreeScene scrollY={scrollY} />

        {/* Radial glow */}
        <div
          style={{
            position: "absolute", inset: 0, zIndex: 0,
            background: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(124,106,247,0.1) 0%, transparent 70%)",
          }}
        />

        <div
          className="hero-grid"
          style={{
            position: "relative", zIndex: 1,
            maxWidth: 1100, margin: "0 auto", padding: "120px 2rem 80px",
            width: "100%",
          }}
        >
          {/* Text */}
          <div>
            <div
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "rgba(124,106,247,0.1)", border: "1px solid rgba(124,106,247,0.3)",
                color: "#a78bfa", padding: "6px 18px", borderRadius: 100,
                fontSize: "0.78rem", fontFamily: "'JetBrains Mono',monospace",
                marginBottom: "1.5rem", letterSpacing: "0.04em",
              }}
            >
              <span
                style={{
                  width: 7, height: 7, borderRadius: "50%", background: "#06d6a0",
                  animation: "blink 1.5s infinite",
                  display: "inline-block",
                }}
              />
              Available for opportunities
            </div>

            <h1
              style={{
                fontFamily: "'Syne',sans-serif",
                fontSize: "clamp(2.8rem,5.5vw,4.8rem)",
                fontWeight: 800, lineHeight: 1.05,
                color: "#e8e8f0",
              }}
            >
              Hello, I'm
              <br />
              <span
                style={{
                  background: "linear-gradient(135deg,#7c6af7 0%,#06d6a0 50%,#f72585 100%)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}
              >
                Akash Babu
              </span>
            </h1>

            <p
              style={{
                color: "#888899", fontSize: "1.05rem", lineHeight: 1.8,
                margin: "1.5rem 0 2rem", maxWidth: 520,
              }}
            >
              AI Engineer & MERN Stack Developer — crafting intelligent systems with Machine Learning,
              Deep Learning, <strong style={{ color: "#a78bfa" }}>Generative AI</strong>, and Agentic AI.
            </p>

            <div className="hero-btns">
              <button
                onClick={() => scrollTo("projects")}
                style={{
                  padding: "13px 28px",
                  background: "linear-gradient(135deg,#7c6af7,#a78bfa)",
                  color: "#fff", borderRadius: 10,
                  fontWeight: 600, fontSize: "0.95rem",
                  border: "none", cursor: "pointer",
                  fontFamily: "'Outfit',sans-serif",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 24px rgba(124,106,247,0.35)",
                }}
              >
                View Projects →
              </button>
              <button
                onClick={() => scrollTo("contact")}
                style={{
                  padding: "13px 28px",
                  background: "transparent",
                  color: "#e8e8f0",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 10, fontWeight: 600, fontSize: "0.95rem",
                  cursor: "pointer", fontFamily: "'Outfit',sans-serif",
                  transition: "all 0.3s",
                }}
              >
                Get in Touch
              </button>
            </div>

            {/* Socials */}
            <div className="hero-socials">
              {[
                { label: "in", href: "https://www.linkedin.com/in/akash-babu-31287a2a5" },
                { label: "gh", href: "https://github.com/AkashBabuYash" },
                { label: "ig", href: "https://www.instagram.com/akashbabu2741" },
                { label: "gfg", href: "https://geeksforgeeks.org/profile/babuakas8j0w" },
                { label: "lc", href: "https://leetcode.com/u/4DM8kNObnw" },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    width: 44, height: 44, borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#888899", textDecoration: "none",
                    fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem", fontWeight: 600,
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#7c6af7";
                    e.currentTarget.style.color = "#a78bfa";
                    e.currentTarget.style.background = "rgba(124,106,247,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                    e.currentTarget.style.color = "#888899";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {s.label}
                </a>
              ))}
            </div>
          </div>

          {/* Stat cards column — replaces profile picture */}
          <div className="hide-mob" style={{ display: "flex", flexDirection: "column", gap: 16, justifyContent: "center" }}>
            {[
              { num: "12+", label: "Projects Built", color: "#7c6af7", icon: "🚀" },
              { num: "3",   label: "Hackathons",     color: "#06d6a0", icon: "🏆" },
              { num: "∞",   label: "Curiosity",      color: "#f72585", icon: "✨" },
            ].map(({ num, label, color, icon }) => (
              <div
                key={label}
                style={{
                  background: "#13131e",
                  border: `1px solid ${color}22`,
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 16, padding: "1.2rem 1.8rem",
                  display: "flex", alignItems: "center", gap: 16,
                  boxShadow: `0 4px 24px ${color}11`,
                  minWidth: 220,
                }}
              >
                <span style={{ fontSize: "1.6rem" }}>{icon}</span>
                <div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: "1.8rem", fontWeight: 800, color }}>{num}</div>
                  <div style={{ fontSize: "0.8rem", color: "#888899" }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          style={{
            position: "absolute", bottom: "2rem", left: "50%", transform: "translateX(-50%)",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
            color: "#888899", fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace",
            letterSpacing: "0.1em",
          }}
        >
          <span>SCROLL</span>
          <div
            style={{
              width: 1, height: 50, background: "linear-gradient(#7c6af7, transparent)",
              animation: "fadeInDown 2s infinite",
            }}
          />
        </div>
      </div>

      <div className="divider" />

      {/* ── ABOUT ── */}
      <section id="about">
        <span className="section-eyebrow reveal">// about me</span>
        <h2 className="section-h reveal">Who I <span className="grad-text">Am</span></h2>
        <div className="grid-2col">
          <div className="reveal">
            <p style={{ color: "#888899", lineHeight: 1.85, marginBottom: "1.2rem" }}>
              I am <strong style={{ color: "#e8e8f0" }}>Akash Babu</strong>, a passionate AI Engineer & Machine Learning specialist pursuing{" "}
              <strong style={{ color: "#a78bfa" }}>B.Tech in AI & ML</strong> from SR Group of Institutions, Lucknow.
            </p>
            <p style={{ color: "#888899", lineHeight: 1.85, marginBottom: "1.2rem" }}>
              I specialize in developing AI-powered solutions — from intelligent chatbots and LLM applications to Agentic AI systems, RAG pipelines, and full-stack MERN applications.
            </p>
            <p style={{ color: "#888899", lineHeight: 1.85, marginBottom: "2rem" }}>
              I actively participate in hackathons and stay current with AI advancements through research papers, documentation, and engineering blogs.
            </p>
            <div className="about-stats">
              {[["12+", "Projects Built"], ["3", "Hackathons"], ["Fresher", "Experience"], ["∞", "Curiosity"]].map(([n, l]) => (
                <div
                  key={l}
                  style={{
                    background: "#13131e", border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: 16, padding: "1.4rem", textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'Syne',sans-serif", fontSize: "2rem", fontWeight: 800,
                      background: "linear-gradient(135deg,#7c6af7,#06d6a0)",
                      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                    }}
                  >
                    {n}
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "#888899", marginTop: 4 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="reveal" style={{ paddingLeft: "2rem", position: "relative" }}>
            <div
              style={{
                position: "absolute", left: 8, top: 0, bottom: 0,
                width: 1, background: "rgba(255,255,255,0.07)",
              }}
            />
            {[
              { year: "2023 — Present", title: "B.Tech — AI & ML", sub: "SR Institute of Management And Technology, Lucknow", color: "#7c6af7" },
              { year: "2021", title: "Intermediate (Class XII)", sub: "Lucknow Convent Public Inter College", color: "#06d6a0" },
              { year: "2019", title: "High School (Class X)", sub: "Lucknow Convent Public Inter College", color: "#f72585" },
              { year: "Languages", title: "English & Hindi", sub: "Professional English · Native Hindi", color: "#ffd166" },
            ].map((t, i) => (
              <div key={t.title} style={{ position: "relative", marginBottom: "2rem" }}>
                <div
                  style={{
                    position: "absolute", left: -28, top: 4,
                    width: 12, height: 12, borderRadius: "50%",
                    background: t.color, border: "2px solid #0a0a0f",
                  }}
                />
                <div style={{ fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace", color: "#06d6a0", marginBottom: 4 }}>{t.year}</div>
                <div style={{ fontWeight: 600, marginBottom: 4, color: "#e8e8f0" }}>{t.title}</div>
                <div style={{ fontSize: "0.85rem", color: "#888899" }}>{t.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ── SKILLS ── */}
      <section id="skills">
        <span className="section-eyebrow reveal">// expertise</span>
        <h2 className="section-h reveal">What I <span className="grad-text">Do</span></h2>
        <div className="reveal grid-4col">
          {SKILLS.map((s) => <SkillCard key={s.name} skill={s} />)}
        </div>

        {/* Knowledge tags */}
        <span className="section-eyebrow reveal">// knowledge base</span>
        <h2 className="section-h reveal" style={{ marginBottom: "1.5rem" }}>
          Deep <span className="grad-text">Knowledge</span>
        </h2>
        <div className="reveal grid-knowledge">
          {[
            { title: "Machine Learning", color: "#4ade80", tags: ["scikit-learn", "pandas", "NumPy", "Supervised", "Unsupervised", "Online ML"] },
            { title: "Deep Learning", color: "#60a5fa", tags: ["TensorFlow", "Keras", "ANN", "CNN", "RNN", "LSTM", "GRU"] },
            { title: "Generative & Agentic AI", color: "#c084fc", tags: ["LangChain", "LangGraph", "HuggingFace", "RAG", "CRAG", "Self-RAG"] },
            { title: "LLM Knowledge", color: "#7c6af7", tags: ["LLM Theory", "Fine-tuning", "Transformers", "Attention", "Embeddings", "Prompting"] },
            { title: "MERN Stack", color: "#06d6a0", tags: ["React.js", "Node.js", "Express", "MongoDB", "Socket.io", "REST API"] },
            { title: "Tools & Infrastructure", color: "#f72585", tags: ["Python", "Java", "Docker", "FastAPI", "Git/GitHub", "Matplotlib"] },
          ].map((k) => (
            <div
              key={k.title}
              style={{
                background: "#13131e",
                border: "1px solid rgba(255,255,255,0.07)",
                borderLeft: `3px solid ${k.color}`,
                borderRadius: 16, padding: "1.75rem",
              }}
            >
              <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: "1rem", color: k.color, marginBottom: "1rem" }}>{k.title}</h3>
              {k.tags.map((t) => (
                <span
                  key={t}
                  style={{
                    display: "inline-block",
                    background: k.color + "15", color: k.color,
                    border: `1px solid ${k.color}33`,
                    borderRadius: 6, fontSize: "0.74rem",
                    fontFamily: "'JetBrains Mono',monospace",
                    padding: "3px 10px", margin: "3px 3px 3px 0",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <div className="divider" />

      {/* ── PROJECTS ── */}
      <section id="projects">
        <span className="section-eyebrow reveal">// portfolio</span>
        <h2 className="section-h reveal">What I've <span className="grad-text">Built</span></h2>

        {/* Category legend */}
        <div className="reveal" style={{ display: "flex", gap: 12, marginBottom: "2.5rem", flexWrap: "wrap" }}>
          {PROJECT_SECTIONS.map((sec) => (
            <div
              key={sec.id}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: sec.color + "12",
                border: `1px solid ${sec.color}33`,
                borderRadius: 100, padding: "6px 16px",
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: sec.color, display: "inline-block" }} />
              <span style={{ fontSize: "0.78rem", color: sec.color, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{sec.label}</span>
              <span style={{ fontSize: "0.72rem", color: sec.color + "99" }}>({sec.projects.length})</span>
            </div>
          ))}
        </div>

        {/* All projects grid — auto display, no tab selection needed */}
        <div className="grid-projects">
          {ALL_PROJECTS.map((p) => (
            <ProjectCard key={p.title} project={p} color={p.color} categoryLabel={p.category} />
          ))}
        </div>
      </section>

      <div className="divider" />

      {/* ── HACKATHONS ── */}
      <section id="hackathons">
        <span className="section-eyebrow reveal">// achievements</span>
        <h2 className="section-h reveal">
          Hackathon <span className="grad-text">Participation</span>
        </h2>
        <div className="reveal grid-hackathons">
          {HACKATHONS.map((h) => (
            <div
              key={h.name}
              style={{
                background: "#13131e",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 20, overflow: "hidden",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(6,214,160,0.3)";
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow = "0 12px 40px rgba(6,214,160,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  padding: "1.5rem",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  display: "flex", alignItems: "center", gap: 14,
                }}
              >
                <div
                  style={{
                    width: 48, height: 48, borderRadius: 13,
                    background: "rgba(6,214,160,0.1)", border: "1px solid rgba(6,214,160,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1.5rem", flexShrink: 0,
                  }}
                >
                  {h.icon}
                </div>
                <div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, color: "#e8e8f0" }}>{h.name}</div>
                  <div style={{ fontSize: "0.83rem", color: "#888899", marginTop: 3 }}>Project: {h.project}</div>
                </div>
              </div>
              <div style={{ padding: "1rem 1.5rem" }}>
                <span
                  style={{
                    display: "inline-block",
                    background: "rgba(6,214,160,0.1)", color: "#06d6a0",
                    border: "1px solid rgba(6,214,160,0.2)",
                    borderRadius: 100, fontSize: "0.74rem", fontWeight: 600,
                    padding: "4px 14px",
                  }}
                >
                  {h.badge}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="divider" />

      {/* ── CONTACT ── */}
      <section id="contact">
        <span className="section-eyebrow reveal">// reach out</span>
        <h2 className="section-h reveal">Let's <span className="grad-text">Connect</span></h2>
        <div className="contact-links-grid">
          <div className="reveal">
            <p style={{ color: "#888899", lineHeight: 1.85, marginBottom: "2rem" }}>
              Open to AI engineering roles, freelance projects, ML/AI research collaborations, and hackathon invitations. Let's build something intelligent together.
            </p>
            {[
              { icon: "📧", label: "Email", value: "babuakash43168@gmail.com", href: "mailto:babuakash43168@gmail.com" },
              { icon: "📞", label: "Phone", value: "+91 8318628430", href: "tel:+918318628430" },
              { icon: "🔗", label: "LinkedIn", value: "akash-babu-31287a2a5", href: "https://www.linkedin.com/in/akash-babu-31287a2a5" },
              { icon: "⌨️", label: "GitHub", value: "AkashBabuYash", href: "https://github.com/AkashBabuYash" },
            ].map((c) => (
              <a
                key={c.label}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "1rem 1.4rem",
                  background: "#13131e", border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 14, marginBottom: "0.75rem",
                  textDecoration: "none", color: "#e8e8f0",
                  transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(124,106,247,0.4)";
                  e.currentTarget.style.transform = "translateX(6px)";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(124,106,247,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: 42, height: 42, borderRadius: 10,
                    background: "rgba(124,106,247,0.1)", border: "1px solid rgba(124,106,247,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1.1rem", flexShrink: 0,
                  }}
                >
                  {c.icon}
                </div>
                <div>
                  <div style={{ fontSize: "0.76rem", color: "#888899", marginBottom: 2 }}>{c.label}</div>
                  <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{c.value}</div>
                </div>
              </a>
            ))}
          </div>

          <div className="reveal">
            {[
              { icon: "📄", label: "Download", title: "My Resume", href: "/icons/resume.pdf", download: "Resume_Akash_Babu.pdf" },
              { icon: "💬", label: "Live Demo", title: "Chatting Application", href: "https://era-techlimited.netlify.app" },
              { icon: "🟢", label: "Competitive Coding", title: "GeeksForGeeks Profile", href: "https://geeksforgeeks.org/profile/babuakas8j0w" },
            ].map((a) => (
              <a
                key={a.title}
                href={a.href}
                target={a.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                download={a.download}
                style={{
                  display: "flex", alignItems: "center", gap: 16,
                  padding: "1.2rem 1.5rem",
                  background: "#13131e", border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 16, marginBottom: "1rem",
                  textDecoration: "none", color: "#e8e8f0",
                  transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(6,214,160,0.3)";
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(6,214,160,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: "linear-gradient(135deg,#7c6af7,#06d6a0)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1.4rem", flexShrink: 0,
                  }}
                >
                  {a.icon}
                </div>
                <div>
                  <div style={{ fontSize: "0.74rem", color: "#888899" }}>{a.label}</div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: "0.92rem" }}>{a.title}</div>
                </div>
                <span style={{ marginLeft: "auto", color: "#888899", fontSize: "1rem" }}>↗</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer
        style={{
          background: "#111118", borderTop: "1px solid rgba(255,255,255,0.07)",
          padding: "3rem 2rem", textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Syne',sans-serif", fontSize: "2rem", fontWeight: 800,
            background: "linear-gradient(135deg,#7c6af7,#06d6a0)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            display: "block", marginBottom: "1rem",
          }}
        >
          Akash Babu.
        </div>
        <p style={{ color: "#888899", fontSize: "0.88rem" }}>AI Engineer & MERN Stack Developer · Lucknow, India</p>
        <p style={{ color: "#555566", fontSize: "0.82rem", marginTop: "0.5rem" }}>
          © 2026 Akash Babu. Crafted with Three.js, React & passion for AI.
        </p>
      </footer>

      <style>{`
        @keyframes blink { 0%,100%{opacity:1;}50%{opacity:0.2;} }
        @keyframes fadeInDown { 0%{opacity:0;transform:translateY(-10px);}100%{opacity:1;transform:translateY(0);} }
        @media(max-width:900px) {
          .hamburger-btn { display:flex !important; }
          .hide-mob { display:none !important; }
        }
      `}</style>
    </>
  );
}